import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { offlineDb, offlineIdentityVerified, OutboxEntry, scopedKey, setRemovedChangesReporter } from './offline-db';
import { OutboxService } from './outbox.service';
import { rememberRowBases } from './tally-row-bases';
import { isPositionClash, placeRows, PositionClashBody, withoutPositionFields } from './position-clash';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { OfflineLeaseService } from '../services/offline-lease.service';

// 'auth' = the server said the login has expired (401): stop the whole run, keep every item waiting,
// and continue after the next login — never a permanent rejection (OFFLINE_PLAN.md 1.2).
// 'unreachable' = the request never reached the server (status 0): the connection is gone or the server
// is unreachable — stop this run and try again soon; it is not the item's fault and is never counted
// as a failed try (Phase 2).
type ReplayResult = 'synced' | 'rejected' | 'pending' | 'auth' | 'unreachable';

/** What a sync run ended with — read by logout and the 15-day lock screen (OFFLINE_PLAN.md 1.6 / 1.7). */
export interface SyncRunOutcome {
  /** The run could not start or stopped early because the device is offline. */
  offline: boolean;
  /** The login expired mid-run; items are still waiting for the next login. */
  authExpired: boolean;
  /** The different-user check has not run yet for this login — nothing was sent. */
  notVerified: boolean;
}

// Replays the offline write queue (outbox) once back online, in multiple passes (see runSync):
// CREATE_TU first (idempotent via clientUuid — see TransportUnitServiceImpl.createTransportUnit),
// then each TU's queued SAVE_TALLY_DETAILS/SAVE_TALLY_SETTINGS/CREATE_PROCESSING_RUN ops with
// their provisional tallyMasterId rewritten to the real one via idMap. CREATE_BP/CREATE_PRODUCT/
// CREATE_LOADING_SITE are simple leaf creates (no dependent child ops) — same idempotent-POST-
// then-reconcile-cache shape, just no id-map needed. A DashboardTallysheetNewComponent left open
// across a sync hot-swaps its provisional ids live once idMap resolves — see that component's
// watchForOfflineSync.
//
// Known limitations of this cut:
// - A BP/Product/LoadingSite create FORM (not list) left open across its own sync does not
//   hot-swap — only the tally dashboard does today. Low-impact: those forms don't keep
//   writing against their own id the way a tally grid does.
// - Retry cadence is "try again on the next trySync() call" (every online transition + every
//   navigation while online) rather than a timed exponential backoff.
// - Permanently-rejected entries are surfaced in the "Sync Issues" panel (/sync-issues —
//   see SyncIssuesComponent) rather than automatically retried or fixed.
// - CREATE_PROCESSING_RUN has no masterCache/detailCache list to reconcile on success or flag on
//   rejection (unlike the BP/Product/LoadingSite/TU leaf creates) — a Custom-Made row's chip
//   simply won't show a "synced"/"failed" state of its own; a rejected entry still surfaces
//   generically in the Sync Issues panel like everything else, just without a dedicated per-row
//   badge.
@Injectable({ providedIn: 'root' })
export class SyncEngineService {
  private readonly logger = inject(NGXLogger);
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastMessagesService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly outboxService = inject(OutboxService);

  private readonly offlineLeaseService = inject(OfflineLeaseService);

  private readonly apiUrl = AppConstants.API_URL + 'api/v1';
  /** The run in progress, if any — single-flight, and awaitable by syncNow(). */
  private runPromise: Promise<SyncRunOutcome> | null = null;
  private syncedPurged = false;
  /** Wakes the engine when the soonest waiting retry is due (Phase 2) — otherwise a retry would only
   *  happen on the next screen change or reconnect. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last run stopped because the server was unreachable — retry in ~30s (see syncNow's finally). */
  private retrySoon = false;
  /** Row saves for one tally sent together in one request, at most this many rows (Phase 2). */
  private static readonly MAX_ROWS_PER_BATCH = 1000;
  /** Last time a sync run finished without the connection dropping (epoch millis) — "Last synced". */
  readonly lastSyncAt = signal<number | null>(SyncEngineService.readLastSyncAt());

  private static readonly LAST_SYNC_KEY = 'll_last_sync_at';
  private static readLastSyncAt(): number | null {
    try {
      const v = Number(localStorage.getItem(SyncEngineService.LAST_SYNC_KEY));
      return Number.isFinite(v) && v > 0 ? v : null;
    } catch { return null; }
  }

  /** Recorded by this engine and by OfflinePrefetchService after a completed refresh. */
  markSynced(at: number = Date.now()): void {
    this.lastSyncAt.set(at);
    try { localStorage.setItem(SyncEngineService.LAST_SYNC_KEY, String(at)); } catch { /* storage off */ }
  }
  // Bumped once per runSync() pass that actually processed something — the only signal a
  // currently-mounted list component (Product/LoadingSite/TU) has that its in-memory pending
  // rows may now be stale. Without this, a row stays showing "Pending Sync" (or the old
  // "Needs Rename" state after a sync that just fixed it) until the user navigates away and
  // back, re-triggering a fresh loadData/sortProducts call — reconnecting while already sitting
  // on the list otherwise never refreshes it in place.
  readonly syncCompletedTick = signal(0);

  /** Items still waiting in the current sync run (UPLOAD_PROGRESS_PLAN.md step 3, 2026-09-29) —
   *  drives the small "3 items syncing…" note in the app shell. 0 when nothing is syncing. */
  readonly syncingCount = signal(0);

  constructor() {
    // Native mobile only (Android/iOS) — the write queue this replays can only ever contain
    // entries on native, since every producer that enqueues into it is itself gated the same
    // way (see transport-unit.service.ts, tallysheet-list.service.ts, etc.). Skip registering
    // the effect at all on web so it never touches offlineDb.outbox.
    if (!isOfflineCapableNative()) return;
    // Re-fires whenever the online signal flips true — including once immediately if the app
    // is constructed already online (covers "was offline, came back online before this
    // service ever loaded" — the exact case a user hit after this engine shipped). Also re-fires
    // once the different-user check has finished for this login (OFFLINE_PLAN.md, 2026-09-29) —
    // the sync never runs before it, see trySync().
    effect(() => {
      if (this.networkStatusService.online() && offlineIdentityVerified()) {
        this.trySync();
      }
    });
    // Tells the previous user "your offline changes were removed" (OFFLINE_PLAN.md 1.5 / D5). The proof is
    // that user's own signed offline lease; without one there is nothing the server would accept.
    setRemovedChangesReporter(async (summary) => {
      const lease = await this.offlineLeaseService.takeLeaseForRemovedNotice();
      if (!lease) return;
      await firstValueFrom(this.http.post(`${this.apiUrl}/sync/removed-changes-notice`,
        { lease, count: summary.count, kinds: summary.kinds }));
    });
  }

  // Safe to call repeatedly (e.g. from every online-route navigation) — no-ops if a run is
  // already in progress, offline, or the different-user check hasn't run yet for this login.
  async trySync(): Promise<void> {
    if (!isOfflineCapableNative() || this.runPromise || !this.networkStatusService.isOnline || !offlineIdentityVerified()) {
      return;
    }
    await this.syncNow();
  }

  /** Runs a sync now and reports how it ended — used by logout and the 15-day lock (OFFLINE_PLAN.md 1.6 /
   *  1.7). If a run is already going, waits for it, then runs once more for anything it left behind.
   *  force = ignore the retry waits (logout, "Sync now") — the user is asking for it right now. */
  async syncNow(force = false): Promise<SyncRunOutcome> {
    const none: SyncRunOutcome = { offline: false, authExpired: false, notVerified: false };
    if (!isOfflineCapableNative()) return none;
    if (this.runPromise) {
      await this.runPromise.catch(() => undefined);
    }
    if (!this.networkStatusService.isOnline) return { ...none, offline: true };
    if (!offlineIdentityVerified()) return { ...none, notVerified: true };
    this.runPromise = this.runSync(force)
      .catch((error) => {
        this.logger.error('[SyncEngine] Unexpected error during sync run', error);
        return none;
      })
      .finally(() => {
        this.runPromise = null;
        this.syncingCount.set(0);
        this.scheduleNextRetry(this.retrySoon);
      });
    return this.runPromise;
  }

  /** Arms one timer for the soonest waiting retry (entries waiting after a server failure). */
  private async scheduleNextRetry(unreachable = false): Promise<void> {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    try {
      const pending = await this.outboxService.getPending();
      if (!pending.length) return;
      const now = Date.now();
      let next = unreachable ? now + 30_000 : Infinity;
      for (const e of pending) {
        if (e.nextAttemptAt && e.nextAttemptAt > now) next = Math.min(next, e.nextAttemptAt);
      }
      if (!Number.isFinite(next)) return;
      this.retryTimer = setTimeout(() => { this.retryTimer = null; this.trySync(); }, Math.max(1_000, next - now));
    } catch { /* best-effort — the next reconnect or screen change still triggers a sync */ }
  }

  // Multi-pass: a single pass over the pending list (old behavior) assumed every entry's
  // dependencies always appear EARLIER in the list — true for the original 2-level chain
  // (CREATE_TU -> SAVE_TALLY_DETAILS/SETTINGS/UPLOAD_DOCUMENT, always enqueued in that real-world
  // order) but not guaranteed for the newer 3-level chain (CREATE_TU -> SAVE_TALLY_DETAILS ->
  // CREATE_PROCESSING_RUN), and not something this engine should assume goes on trusting blindly.
  // Re-fetches the pending list fresh each pass (entries synced/rejected in an earlier pass drop
  // out of getPending() automatically) and keeps looping until a full pass makes no progress
  // (nothing newly synced or rejected) — at that point everything left is either blocked on a
  // dependency that itself is stuck, or waiting on the next trySync() call, so further looping
  // right now would just spin. Bounded by MAX_PASSES as a hard safety valve, not a normal exit.
  private async runSync(force = false): Promise<SyncRunOutcome> {
    const MAX_PASSES = 10;
    let totalSynced = 0;
    let totalFailed = 0;
    let sawAnyEntry = false;
    let wentOfflineMidRun = false;
    let authExpired = false;
    let unreachable = false;

    if (!this.syncedPurged) {
      this.syncedPurged = true;
      await this.outboxService.purgeSynced().catch(() => undefined);
    }

    for (let pass = 0; pass < MAX_PASSES && !wentOfflineMidRun && !authExpired && !unreachable; pass++) {
      const now = Date.now();
      // Entries still in their retry wait are left for later (unless forced) — see scheduleNextRetry().
      // Order is kept per record: once one entry of a clientUuid is still waiting, every LATER entry of that
      // same clientUuid waits too — a newer row save sent before an older one would be overwritten by it.
      const waitingOwners = new Set<string>();
      const pending = (await this.outboxService.getPending()).filter((e) => {
        if (waitingOwners.has(e.clientUuid)) return false;
        const due = force || !e.nextAttemptAt || e.nextAttemptAt <= now;
        if (!due) waitingOwners.add(e.clientUuid);
        return due;
      });
      if (!pending.length) break;
      sawAnyEntry = true;
      this.syncingCount.set(pending.length);
      this.logger.info(`[SyncEngine] Pass ${pass + 1}: replaying ${pending.length} queued offline operation(s)`);

      let passSynced = 0;
      let passFailed = 0;
      // A dependency (e.g. a TU whose CREATE_TU didn't finish this pass) that's blocking one
      // entry blocks every OTHER entry sharing that same clientUuid too, for the rest of THIS
      // pass only — re-fetching fresh at the top of the next pass gives every entry, including
      // ones skipped here, a fair re-attempt once whatever they were waiting on has since synced.
      const blockedClientUuids = new Set<string>();
      const handled = new Set<number>();

      for (let i = 0; i < pending.length; i++) {
        const entry = pending[i];
        if (handled.has(entry.seq!)) continue;
        if (!this.networkStatusService.isOnline) {
          wentOfflineMidRun = true; // remaining entries (this pass and any later one) stay pending for next time
          break;
        }
        if (blockedClientUuids.has(entry.clientUuid)) {
          continue;
        }

        // Several row saves queued for the same tally go as ONE request (Phase 2) — each save is
        // otherwise its own round trip. Only consecutive saves for that tally (a queued delete in
        // between keeps the order: stop there), with the same extra parameters.
        const group = entry.opType === 'SAVE_TALLY_DETAILS' ? this.collectSaveGroup(pending, i) : [entry];
        group.forEach((e) => handled.add(e.seq!));
        const result = group.length > 1 ? await this.replaySaveTallyDetailsGroup(group) : await this.replayEntry(entry);
        this.syncingCount.update(n => Math.max(0, n - group.length));

        if (result === 'auth') {
          authExpired = true; // everything stays waiting — continues after the next login
          break;
        }
        if (result === 'unreachable') {
          unreachable = true; // connection gone mid-run — everything stays waiting, retried in ~30s
          break;
        }
        if (result === 'synced') {
          passSynced += group.length;
        } else {
          if (result === 'rejected') {
            passFailed += group.length;
          }
          blockedClientUuids.add(entry.clientUuid);
        }
      }

      totalSynced += passSynced;
      totalFailed += passFailed;
      if (!wentOfflineMidRun && !authExpired && !unreachable && passSynced === 0 && passFailed === 0) break; // no progress this pass — stop looping
    }
    this.retrySoon = unreachable;
    if (!wentOfflineMidRun && !authExpired && !unreachable) {
      this.markSynced();
    }

    this.syncingCount.set(0);
    if (totalSynced || totalFailed) {
      const detail = totalFailed
        ? `${totalSynced} offline item(s) synced, ${totalFailed} could not be saved — see Sync Issues.`
        : `${totalSynced} offline item(s) synced.`;
      if (totalFailed) {
        this.toast.showWarningMessage('Offline sync', detail, true);
      } else {
        this.toast.showSuccessMessage('Offline sync', detail);
      }
    }
    if (sawAnyEntry) {
      this.syncCompletedTick.update((v) => v + 1);
    }
    return { offline: wentOfflineMidRun || unreachable, authExpired, notVerified: false };
  }

  // Same masterCache key mapping as replayEntry's leaf-create routing — kept as a lookup
  // table here too since discardRejected (SyncIssuesComponent) needs it independent of an
  // actual replay attempt.
  private static readonly LEAF_MASTER_CACHE_KEYS: Partial<Record<OutboxEntry['opType'], string[]>> = {
    CREATE_BP: ['buyers', 'suppliers'],
    CREATE_PRODUCT: ['products'],
    CREATE_LOADING_SITE: ['loadingSites'],
  };

  // User-initiated, from the Sync Issues panel — only ever called on an already-`rejected`
  // (terminal) entry. Unlike the flag* helpers used during a replay pass (which mark the
  // placeholder as failed but keep it visible with a badge), this fully removes it: the
  // record never existed on the server and the user has explicitly chosen to give up on it,
  // so leaving a badge-less orphan behind would be worse than removing it outright.
  async discardRejected(entry: OutboxEntry): Promise<void> {
    if (entry.opType === 'CREATE_TU') {
      const detailEntries = await offlineDb.detailCache
        .filter((e) => (e.data as any)?._offlineClientUuid === entry.clientUuid)
        .toArray();
      await Promise.all(detailEntries.map((e) => offlineDb.detailCache.delete(e.key)));
      await offlineDb.pendingRows.delete(entry.clientUuid);
      // Discarding the create also orphans every dependent SAVE_TALLY_DETAILS/
      // SAVE_TALLY_SETTINGS op queued against it — idMap will never gain an entry for this
      // clientUuid now, so they'd otherwise sit in the outbox forever, retried on every sync
      // run and always resolving to 'pending'. Sweep them out together.
      const dependents = await this.outboxService.getByClientUuid(entry.clientUuid);
      for (const dependent of dependents) {
        if (dependent.seq !== entry.seq) {
          await this.outboxService.discard(dependent.seq!);
        }
      }
    } else if (entry.opType === 'SAVE_TALLY_DETAILS') {
      // A refused row save that the user gives up on must also leave the grid (OFFLINE_PLAN.md 1.1).
      await this.clearSyncedPendingRows(entry.clientUuid, entry.payload as any[]);
    } else {
      const masterCacheKeys = SyncEngineService.LEAF_MASTER_CACHE_KEYS[entry.opType];
      if (masterCacheKeys) {
        for (const key of masterCacheKeys) {
          const cacheEntry = await offlineDb.masterCache.get(scopedKey(key));
          const list = Array.isArray(cacheEntry?.data) ? (cacheEntry!.data as any[]) : null;
          if (!list) continue;
          const filtered = list.filter((item) => item?._offlineClientUuid !== entry.clientUuid);
          if (filtered.length !== list.length) {
            await offlineDb.masterCache.put({ key: scopedKey(key), data: filtered, fetchedAt: cacheEntry!.fetchedAt });
          }
        }
      }
    }
    await this.outboxService.discard(entry.seq!);
  }

  private async replayEntry(entry: OutboxEntry): Promise<ReplayResult> {
    switch (entry.opType) {
      case 'CREATE_TU': return this.replayCreateTu(entry);
      case 'SAVE_TALLY_DETAILS': return this.replaySaveTallyDetails(entry);
      case 'DELETE_TALLY_DETAILS': return this.replayDeleteTallyDetails(entry);
      case 'CREATE_BP': return this.replayLeafCreate(entry, `${this.apiUrl}/business-partners`, 'bp', ['buyers', 'suppliers']);
      case 'CREATE_PRODUCT': return this.replayLeafCreate(entry, `${this.apiUrl}/products`, 'product', ['products']);
      case 'CREATE_LOADING_SITE': return this.replayLeafCreate(entry, `${this.apiUrl}/loading-site`, 'loadingSite', ['loadingSites']);
      case 'SAVE_TALLY_SETTINGS': return this.replaySaveTallySettings(entry);
      case 'UPLOAD_DOCUMENT': return this.replayUploadDocument(entry);
      case 'UPLOAD_PRODUCT_PHOTO': return this.replayUploadProductPhoto(entry);
      case 'CREATE_PROCESSING_RUN': return this.replayCreateProcessingRun(entry);
      case 'UPDATE_GRID_STYLE': return this.replayUpdateGridStyle(entry);
    }
  }

  // Shared replay for BP/Product/LoadingSite — all three are simple idempotent-POST leaf
  // creates (backend dedupes on clientUuid, same as CREATE_TU) with no dependent child ops.
  // masterCacheKeys is the list(s) in offlineDb.masterCache that need their fabricated
  // (offline-created) entry swapped for the real synced one — BP writes into both 'buyers'
  // and/or 'suppliers' depending on partyType, so a BP entry could need reconciling in either
  // or both; Product/LoadingSite each have exactly one list.
  private async replayLeafCreate(entry: OutboxEntry, url: string, detailPrefix: string, masterCacheKeys: string[]): Promise<ReplayResult> {
    const payload = entry.payload as any;
    try {
      const response: any = await firstValueFrom(this.http.post(url, payload));
      await this.finalizeLeafCreateSuccess(entry, response, detailPrefix, masterCacheKeys);
      return 'synced';
    } catch (error: any) {
      const result = await this.handleReplayError(entry, error);
      if (result === 'rejected') {
        const errorCode: string | undefined = error?.error?.code;
        await this.flagMasterCacheSyncFailed(entry.clientUuid, masterCacheKeys, errorCode);
      }
      return result;
    }
  }

  // Shared by a normal replay success and a Sync Issues "Rename & Retry" success (see
  // retryLeafCreateWithEdits below) — swaps the fabricated offline placeholder for the real
  // synced record wherever it's cached.
  private async finalizeLeafCreateSuccess(entry: OutboxEntry, response: any, detailPrefix: string, masterCacheKeys: string[]): Promise<void> {
    const provisionalEncodedId = `offline-${entry.clientUuid}`;
    // Same company-labelled key the detail screens read (2026-09-29 — was a plain key, so offline copies were never found).
    await offlineDb.detailCache.delete(scopedKey(`${detailPrefix}:${provisionalEncodedId}`));

    for (const key of masterCacheKeys) {
      const cacheEntry = await offlineDb.masterCache.get(scopedKey(key));
      const list = Array.isArray(cacheEntry?.data) ? (cacheEntry!.data as any[]) : null;
      if (!list) continue;
      const idx = list.findIndex((item) => item?._offlineClientUuid === entry.clientUuid);
      if (idx === -1) continue;
      const updated = [...list];
      updated[idx] = response;
      await offlineDb.masterCache.put({ key: scopedKey(key), data: updated, fetchedAt: cacheEntry!.fetchedAt });
    }

    await this.outboxService.markSynced(entry.seq!);
    this.logger.info(`[SyncEngine] ${entry.opType} synced clientUuid=${entry.clientUuid}`);
  }

  // Only Product/LoadingSite are wired here — those are the two backends that actually throw
  // NameAlreadyExistsException (see ProductServiceImpl.createProduct/LoadingSiteServiceImpl.
  // saveLoadingSite). CREATE_BP has no server-side name check today, so listing it here would
  // advertise a "Rename & Retry" path that can never actually trigger.
  private static readonly LEAF_CREATE_ROUTES: Partial<Record<OutboxEntry['opType'], { url: string; detailPrefix: string; masterCacheKeys: string[] }>> = {
    CREATE_PRODUCT: { url: `${AppConstants.API_URL}api/v1/products`, detailPrefix: 'product', masterCacheKeys: ['products'] },
    CREATE_LOADING_SITE: { url: `${AppConstants.API_URL}api/v1/loading-site`, detailPrefix: 'loadingSite', masterCacheKeys: ['loadingSites'] },
  };

  // Called from SyncIssuesComponent's "Rename & Retry" action on a NAME_ALREADY_EXISTS-rejected
  // CREATE_PRODUCT/CREATE_LOADING_SITE entry — resubmits the original offline payload
  // with an edited name as a fresh create (the device is necessarily online for this rejection
  // to have happened at all, so this is a live call, not a queued replay). On success, patches
  // the cache exactly like a normal replay and removes the old rejected outbox entry; on a
  // renewed NAME_ALREADY_EXISTS the caller gets the fresh existingId/existingEncodedId back so
  // the panel can show it without a page reload.
  async retryLeafCreateWithEdits(entry: OutboxEntry, newName: string): Promise<
    { outcome: 'synced' } | { outcome: 'rejected'; message: string; errorCode?: string; errorMeta?: { existingId?: number; existingEncodedId?: string } }
  > {
    const route = SyncEngineService.LEAF_CREATE_ROUTES[entry.opType];
    if (!route) {
      return { outcome: 'rejected', message: 'Unsupported entry type for retry.' };
    }
    const payload = { ...(entry.payload as any), name: newName };
    try {
      const response: any = await firstValueFrom(this.http.post(route.url, payload));
      await this.finalizeLeafCreateSuccess(entry, response, route.detailPrefix, route.masterCacheKeys);
      await this.outboxService.discard(entry.seq!);
      return { outcome: 'synced' };
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Unknown error';
      const body = error?.error;
      const errorCode: string | undefined = body?.code;
      const errorMeta = errorCode === 'NAME_ALREADY_EXISTS'
        ? { existingId: body?.existingId, existingEncodedId: body?.existingEncodedId }
        : undefined;
      await this.outboxService.markRejected(entry.seq!, message, errorCode, errorMeta);
      return { outcome: 'rejected', message, errorCode, errorMeta };
    }
  }

  // A rejected (terminal — see handleReplayError) offline create will never sync as-is.
  // NAME_ALREADY_EXISTS specifically is user-fixable (pick a different name), so it gets its
  // own _needsRename flag instead of the generic _offlineSyncFailed — ProductListComponent/
  // LoadingSiteListComponent show a distinct "Needs Rename" badge with an inline rename action
  // for that case, and fall back to the plain "Sync failed" badge for every other rejection
  // reason (validation error, plan limit, etc.) where a rename wouldn't help.
  private async flagMasterCacheSyncFailed(clientUuid: string, masterCacheKeys: string[], errorCode?: string): Promise<void> {
    const flag = errorCode === 'NAME_ALREADY_EXISTS' ? { _needsRename: true } : { _offlineSyncFailed: true };
    for (const key of masterCacheKeys) {
      const cacheEntry = await offlineDb.masterCache.get(scopedKey(key));
      const list = Array.isArray(cacheEntry?.data) ? (cacheEntry!.data as any[]) : null;
      if (!list) continue;
      const idx = list.findIndex((item) => item?._offlineClientUuid === clientUuid);
      if (idx === -1) continue;
      const updated = [...list];
      updated[idx] = { ...updated[idx], ...flag };
      await offlineDb.masterCache.put({ key: scopedKey(key), data: updated, fetchedAt: cacheEntry!.fetchedAt });
    }
  }

  // Same rationale as flagMasterCacheSyncFailed, but for CREATE_TU — its offline placeholder
  // lives in detailCache (tu:id:<provisionalId>), not masterCache. See
  // ViewTransportUnitComponent's _syncFailed/_needsRename handling.
  private async flagDetailCacheSyncFailed(clientUuid: string, errorCode?: string): Promise<void> {
    const flag = errorCode === 'NAME_ALREADY_EXISTS' ? { _needsRename: true } : { _offlineSyncFailed: true };
    const entries = await offlineDb.detailCache
      .filter((e) => (e.data as any)?._offlineClientUuid === clientUuid)
      .toArray();
    for (const entry of entries) {
      await offlineDb.detailCache.put({
        ...entry,
        data: { ...(entry.data as any), ...flag },
      });
    }
  }

  private async replayCreateTu(entry: OutboxEntry): Promise<ReplayResult> {
    const payload = entry.payload as any;
    const {
      _offlineProvisionalTallyMasterId,
      _offlineProvisionalLineTallyMasterIds,
      ...requestBody
    } = payload;

    try {
      const response: any = await firstValueFrom(
        this.http.post(`${this.apiUrl}/transportunits`, requestBody),
      );

      const tallyMasterIdMap: Record<number, number> = {};
      if (_offlineProvisionalTallyMasterId != null && response?.tallyMasterId != null) {
        tallyMasterIdMap[_offlineProvisionalTallyMasterId] = response.tallyMasterId;
      }
      (_offlineProvisionalLineTallyMasterIds || []).forEach((provisionalId: number, i: number) => {
        const realId = response?.additionalLines?.[i]?.tallyMasterId;
        if (realId != null) {
          tallyMasterIdMap[provisionalId] = realId;
        }
      });

      await offlineDb.idMap.put({
        clientUuid: entry.clientUuid,
        realTuId: response.id,
        realEncodedId: response.encodedId,
        tallyMasterIdMap,
        defaultTallyMasterId: response?.tallyMasterId ?? null,
        syncedAt: Date.now(),
      });
      // Same detailCache keys createOffline seeded (tu:id:<provisionalId>, tu:encoded:offline-
      // <uuid>) — swap the placeholder for the real synced object so any cache-backed read
      // (getById/getByEncodedId's offline fallback) resolves to the actual server state instead
      // of the stale pre-sync placeholder. Confirmed missing here (unlike the identical block in
      // retryCreateTuWithEdits) 2026-08-05: a since-synced TU's Product column (Flexible Layout)
      // rendered correctly online but silently fell back to the stale cached snapshot's state
      // offline after a cold app start, since nothing had ever refreshed the cache in between.
      const provisionalEntries = await offlineDb.detailCache
        .filter((e) => (e.data as any)?._offlineClientUuid === entry.clientUuid)
        .toArray();
      for (const e of provisionalEntries) {
        await offlineDb.detailCache.put({ ...e, data: response });
      }
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] CREATE_TU synced clientUuid=${entry.clientUuid} realId=${response.id}`);
      return 'synced';
    } catch (error: any) {
      const result = await this.handleReplayError(entry, error);
      if (result === 'rejected') {
        const errorCode: string | undefined = error?.error?.code;
        await this.flagDetailCacheSyncFailed(entry.clientUuid, errorCode);
      }
      return result;
    }
  }

  // Called from ViewTransportUnitComponent's "Rename & Retry" action on a NAME_ALREADY_EXISTS-
  // rejected CREATE_TU entry — resubmits the original offline payload with an edited
  // transportId as a fresh create. Unlike retryLeafCreateWithEdits (Product/LoadingSite),
  // CREATE_TU has dependent queued ops (SAVE_TALLY_DETAILS/SAVE_TALLY_SETTINGS/UPLOAD_DOCUMENT)
  // that only resolve their real tallyMasterId via the idMap entry replayCreateTu normally
  // writes on success — so this must write that same idMap entry too, or those dependents stay
  // stuck retrying 'pending' forever against a CREATE_TU that will never come back.
  async retryCreateTuWithEdits(entry: OutboxEntry, newTransportId: string): Promise<
    { outcome: 'synced' } | { outcome: 'rejected'; message: string; errorCode?: string; errorMeta?: { existingId?: number; existingEncodedId?: string } }
  > {
    const payload = entry.payload as any;
    const {
      _offlineProvisionalTallyMasterId,
      _offlineProvisionalLineTallyMasterIds,
      ...requestBody
    } = payload;
    requestBody.transportId = newTransportId;

    try {
      const response: any = await firstValueFrom(this.http.post(`${this.apiUrl}/transportunits`, requestBody));

      const tallyMasterIdMap: Record<number, number> = {};
      if (_offlineProvisionalTallyMasterId != null && response?.tallyMasterId != null) {
        tallyMasterIdMap[_offlineProvisionalTallyMasterId] = response.tallyMasterId;
      }
      (_offlineProvisionalLineTallyMasterIds || []).forEach((provisionalId: number, i: number) => {
        const realId = response?.additionalLines?.[i]?.tallyMasterId;
        if (realId != null) {
          tallyMasterIdMap[provisionalId] = realId;
        }
      });

      await offlineDb.idMap.put({
        clientUuid: entry.clientUuid,
        realTuId: response.id,
        realEncodedId: response.encodedId,
        tallyMasterIdMap,
        defaultTallyMasterId: response?.tallyMasterId ?? null,
        syncedAt: Date.now(),
      });

      // Same detailCache keys createOffline seeded (tu:id:<provisionalId>, tu:encoded:offline-
      // <uuid>) — swap the placeholder for the real synced object so any cache-backed read
      // (ViewTransportUnitComponent's fallback, getById/getByEncodedId) resolves correctly.
      const provisionalEntries = await offlineDb.detailCache
        .filter((e) => (e.data as any)?._offlineClientUuid === entry.clientUuid)
        .toArray();
      for (const e of provisionalEntries) {
        await offlineDb.detailCache.put({ ...e, data: response });
      }

      await this.outboxService.discard(entry.seq!);
      this.logger.info(`[SyncEngine] CREATE_TU synced via rename-retry clientUuid=${entry.clientUuid} realId=${response.id}`);
      return { outcome: 'synced' };
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Unknown error';
      const body = error?.error;
      const errorCode: string | undefined = body?.code;
      const errorMeta = errorCode === 'NAME_ALREADY_EXISTS'
        ? { existingId: body?.existingId, existingEncodedId: body?.existingEncodedId }
        : undefined;
      await this.outboxService.markRejected(entry.seq!, message, errorCode, errorMeta);
      return { outcome: 'rejected', message, errorCode, errorMeta };
    }
  }

  // Same dependency resolution as replaySaveTallySettings/replayUpdateGridStyle: a synthetic
  // `tally-<masterId>` clientUuid (see TallysheetListService.saveDetailsOffline) means the
  // master was already real and only the device was offline — use each row's own
  // tallyMasterId as-is; anything else is the owning TU's own clientUuid to resolve via idMap.
  private async replaySaveTallyDetails(entry: OutboxEntry): Promise<ReplayResult> {
    return this.replaySaveTallyDetailsGroup([entry]);
  }

  /** The queued row saves that can go together with pending[start] (see runSync). */
  private collectSaveGroup(pending: OutboxEntry[], start: number): OutboxEntry[] {
    const first = pending[start];
    const params = JSON.stringify(first.params ?? {});
    const group = [first];
    let rows = Array.isArray(first.payload) ? (first.payload as unknown[]).length : 0;
    for (let j = start + 1; j < pending.length; j++) {
      const e = pending[j];
      if (e.clientUuid !== first.clientUuid) continue; // other tallies' items don't break the run
      if (e.opType !== 'SAVE_TALLY_DETAILS' || JSON.stringify(e.params ?? {}) !== params) break;
      const n = Array.isArray(e.payload) ? (e.payload as unknown[]).length : 0;
      if (rows + n > SyncEngineService.MAX_ROWS_PER_BATCH) break;
      group.push(e);
      rows += n;
    }
    return group;
  }

  // One or more queued row saves for the SAME tally (same clientUuid), sent as one request. A row saved
  // more than once goes with its latest version only. Success/failure applies to every entry in the group.
  private async replaySaveTallyDetailsGroup(group: OutboxEntry[]): Promise<ReplayResult> {
    const entry = group[0];
    const byOrderKey = new Map<unknown, any>();
    const unkeyed: any[] = [];
    for (const e of group) {
      for (const row of (e.payload as any[]) ?? []) {
        const k = row?.id?.orderKey;
        if (k === undefined || k === null) unkeyed.push(row); else byOrderKey.set(k, row);
      }
    }
    let rows = [...byOrderKey.values(), ...unkeyed];

    if (!entry.clientUuid.startsWith('tally-')) {
      const idMapEntry = await offlineDb.idMap.get(entry.clientUuid);
      if (!idMapEntry) {
        // Owning CREATE_TU hasn't synced yet — try again on the next run.
        return 'pending';
      }
      rows = rows.map((row) => ({
        ...row,
        id: {
          ...row.id,
          tallyMasterId: idMapEntry.tallyMasterIdMap[row.id.tallyMasterId]
            ?? idMapEntry.defaultTallyMasterId
            ?? row.id.tallyMasterId,
        },
      }));
    }

    // overStockReason was typed offline with these rows (OFFLINE_PLAN.md 1.3) — same query param as the live save.
    let params = new HttpParams();
    if (entry.params?.['overStockReason']) {
      params = params.set('overStockReason', entry.params['overStockReason']);
    }
    // "Changed by someone else" (2026-09-29): rows carry the conflictBase stamped when they were saved
    // offline. A refusal (409 ROW_CONFLICT) lands in Sync Issues via handleReplayError, where "Keep
    // mine" re-queues with force=1 — a deliberate overwrite, sent without the check.
    if (entry.params?.['force'] !== '1') {
      params = params.set('conflictCheck', 'true');
    }

    try {
      const res: any = await firstValueFrom(this.http.post(`${this.apiUrl}/tally/detail/batch`, rows, { params }));
      if (rows.length) rememberRowBases(rows[0]?.id?.tallyMasterId, res?.resultObj);
      for (const e of group) {
        await this.clearSyncedPendingRows(e.clientUuid, e.payload as any[]);
        await this.outboxService.markSynced(e.seq!);
      }
      this.logger.info(`[SyncEngine] SAVE_TALLY_DETAILS synced clientUuid=${entry.clientUuid} rows=${rows.length} saves=${group.length}`);
      return 'synced';
    } catch (error: any) {
      // Someone else added rows at the same place while this phone was offline (POSITION_CLASH_PLAN.md).
      // Never block the queue: place mine after theirs (the default), save again, record the marks.
      if (isPositionClash(error)) {
        try {
          return await this.placeClashingRowsAndSave(group, rows, error.error, params);
        } catch (again: any) {
          let result: ReplayResult = 'pending';
          for (const e of group) result = await this.handleReplayError(e, again);
          return result;
        }
      }
      let result: ReplayResult = 'pending';
      for (const e of group) {
        result = await this.handleReplayError(e, error);
      }
      return result;
    }
  }

  /**
   * Position clash on replay (see the catch above). Rows that can't be placed (no room between two
   * keys) are saved where they were and still marked. After the save, the placed rows' OLD keys are
   * renamed to their new keys in this phone's saved rows and in every later queued save/delete for
   * this tally — otherwise a later edit would re-create the row at its old place. (The server also
   * redirects an old key it still receives, as a second safety net.)
   */
  private async placeClashingRowsAndSave(group: OutboxEntry[], rows: any[], body: PositionClashBody, params: HttpParams): Promise<ReplayResult> {
    const masterId = rows?.[0]?.id?.tallyMasterId;
    const placed = placeRows(rows, body, 'B_FIRST');
    const toSave = placed ? placed.rows : withoutPositionFields(rows);
    const record = placed ? placed.record : {
      masterId,
      choice: 'AS_IS',
      clashes: (body.clashes || []).map((c) => ({
        mine: (c.mineKeys || []).map((k) => ({ key: k, movedFrom: k })),
        theirs: (c.theirs || []).map((t) => t.orderKey),
      })),
    };
    const res: any = await firstValueFrom(this.http.post(`${this.apiUrl}/tally/detail/batch`, toSave, { params }));
    rememberRowBases(masterId, res?.resultObj);
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/tally/row-clash/record`, record));
    } catch {
      // The rows are saved; a missing mark is not worth failing the sync.
    }

    const moved = placed?.moved ?? {};
    const clientUuid = group[0].clientUuid;
    if (Object.keys(moved).length) {
      await this.renameKeysOnPhone(clientUuid, moved, new Set(group.map((e) => e.seq!)));
    }
    for (const e of group) {
      const payload = Object.keys(moved).length ? this.renameRowKeys(e.payload as any[], moved) : (e.payload as any[]);
      await this.clearSyncedPendingRows(e.clientUuid, payload);
      await this.outboxService.markSynced(e.seq!);
    }

    const count = placed ? placed.placedCount : (body.clashes || []).reduce((n, c) => n + (c.mineKeys?.length || 0), 0);
    const names = placed ? placed.otherNames.join(', ') : '';
    this.toast.showWarningMessage('Offline sync',
      `${count} row(s) you added offline were placed after rows ${names || 'someone else'} added at the same place. Open the Stock Unit and use "Clashing rows" to check or change this.`,
      true);
    this.logger.info(`[SyncEngine] SAVE_TALLY_DETAILS position clash handled clientUuid=${clientUuid} placed=${count} asIs=${!placed}`);
    return 'synced';
  }

  private renameRowKeys(rows: any[], moved: Record<string, string>): any[] {
    return (rows || []).map((r) => {
      const to = moved[r?.id?.orderKey];
      return to ? { ...r, id: { ...r.id, orderKey: to } } : r;
    });
  }

  /** Old → new keys in pendingRows and in every still-waiting queue item of this tally (not `skip`). */
  private async renameKeysOnPhone(clientUuid: string, moved: Record<string, string>, skip: Set<number>): Promise<void> {
    const pending = await offlineDb.pendingRows.get(clientUuid);
    if (pending?.rows?.length) {
      await offlineDb.pendingRows.put({ clientUuid, rows: this.renameRowKeys(pending.rows, moved) });
    }
    const later = await this.outboxService.getByClientUuid(clientUuid);
    for (const e of later) {
      if (skip.has(e.seq!) || e.status === 'synced') continue;
      if (e.opType === 'SAVE_TALLY_DETAILS') {
        await offlineDb.outbox.update(e.seq!, { payload: this.renameRowKeys(e.payload as any[], moved) });
      } else if (e.opType === 'DELETE_TALLY_DETAILS') {
        const ids = (e.payload as any[]).map((id) => (moved[id?.orderKey] ? { ...id, orderKey: moved[id.orderKey] } : id));
        await offlineDb.outbox.update(e.seq!, { payload: ids });
      }
    }
  }

  // OFFLINE_PLAN.md 1.1 (2026-09-29). Rows saved offline stay in pendingRows so the grid shows them before
  // they sync (TallysheetListService merges them over the server rows). Once they HAVE synced they must go,
  // or every later load keeps laying these old values over newer server data — and a save from that grid
  // writes them back. Only rows still exactly as synced are removed: a newer offline edit of the same row
  // (its own later queue item) stays until that one syncs too.
  private async clearSyncedPendingRows(clientUuid: string, syncedRows: any[]): Promise<void> {
    if (!Array.isArray(syncedRows) || !syncedRows.length) return;
    const existing = await offlineDb.pendingRows.get(clientUuid);
    if (!existing?.rows?.length) return;
    const synced = new Map<unknown, string>(syncedRows.map((r) => [r?.id?.orderKey, JSON.stringify(r)]));
    const left = existing.rows.filter((r: any) => synced.get(r?.id?.orderKey) !== JSON.stringify(r));
    if (left.length === existing.rows.length) return;
    if (left.length) {
      await offlineDb.pendingRows.put({ clientUuid, rows: left });
    } else {
      await offlineDb.pendingRows.delete(clientUuid);
    }
  }

  // Only ever enqueued for an already-real master (see TallysheetListService.
  // deleteDetailsOffline) — a provisional master's rows never reached the server in the first
  // place, so there's nothing for THIS op to replay in that case (deleteDetailsOffline handles
  // it with a pure local pendingRows removal instead, no outbox entry at all). No idMap
  // resolution needed: the ids in the payload already carry their real tallyMasterId as-is.
  private async replayDeleteTallyDetails(entry: OutboxEntry): Promise<ReplayResult> {
    const ids = entry.payload as any[];
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/tally/detail/delete`, ids));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] DELETE_TALLY_DETAILS synced clientUuid=${entry.clientUuid} rows=${ids.length}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  // masterId in the payload may be provisional (owning TU created offline, not yet synced —
  // resolved via idMap same as replaySaveTallyDetails) or already real (device was simply
  // offline when the change was made — used as-is). clientUuid tells us which case this is:
  // a synthetic `settings-<masterId>` id (see TallysheetListService.saveSettingsOffline)
  // means "already real, no dependency"; anything else is a real TU clientUuid to resolve.
  private async replaySaveTallySettings(entry: OutboxEntry): Promise<ReplayResult> {
    const settings = entry.payload as any;
    let masterId = settings.masterId;

    if (!entry.clientUuid.startsWith('settings-')) {
      const idMapEntry = await offlineDb.idMap.get(entry.clientUuid);
      if (!idMapEntry) {
        return 'pending'; // owning CREATE_TU hasn't synced yet — try again next run
      }
      masterId = idMapEntry.tallyMasterIdMap[masterId] ?? idMapEntry.defaultTallyMasterId ?? masterId;
    }

    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/settings`, { ...settings, masterId }));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] SAVE_TALLY_SETTINGS synced clientUuid=${entry.clientUuid} masterId=${masterId}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  // Same dependency resolution as replaySaveTallySettings: a synthetic `gridstyle-<masterId>`
  // clientUuid (see TransportUnitService.updateGridStyleOffline) means the master was already
  // real and only the device was offline — use payload.tallyMasterId as-is; anything else is the
  // owning TU's own clientUuid to resolve via idMap.
  private async replayUpdateGridStyle(entry: OutboxEntry): Promise<ReplayResult> {
    const { tallyMasterId, gridStyle } = entry.payload as any;
    let masterId = tallyMasterId;

    if (!entry.clientUuid.startsWith('gridstyle-')) {
      const idMapEntry = await offlineDb.idMap.get(entry.clientUuid);
      if (!idMapEntry) {
        return 'pending'; // owning CREATE_TU hasn't synced yet — try again next run
      }
      masterId = idMapEntry.tallyMasterIdMap[masterId] ?? idMapEntry.defaultTallyMasterId ?? masterId;
    }

    try {
      await firstValueFrom(this.http.patch(`${this.apiUrl}/tally/master/${masterId}/grid-style`, {}, { params: { gridStyle } }));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] UPDATE_GRID_STYLE synced clientUuid=${entry.clientUuid} masterId=${masterId}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  // clientUuid prefix tells us which case this is: a synthetic `doc-<masterId>-<uuid>` id
  // (see TallysheetListService.queueDocumentUpload) means the master was already real and only
  // the device was offline — use payload.masterId as-is; anything else is the owning TU's own
  // clientUuid, needing idMap resolution first (same dependency pattern as
  // replaySaveTallySettings).
  private async replayUploadDocument(entry: OutboxEntry): Promise<ReplayResult> {
    const payload = entry.payload as { masterId: number; file: File; category: string; privacyType: string; note: string };
    let masterId = payload.masterId;

    if (!entry.clientUuid.startsWith('doc-')) {
      const idMapEntry = await offlineDb.idMap.get(entry.clientUuid);
      if (!idMapEntry) {
        return 'pending'; // owning CREATE_TU hasn't synced yet — try again next run
      }
      masterId = idMapEntry.tallyMasterIdMap[masterId] ?? idMapEntry.defaultTallyMasterId ?? masterId;
    }

    const formData = new FormData();
    formData.append('files', payload.file, payload.file.name);
    formData.append('categories', payload.category);
    formData.append('privacyTypes', payload.privacyType);
    formData.append('notes', payload.note);
    // The queue item's own unique id: a retried upload whose first try already reached the server is
    // recognised there and not saved twice (Phase 2). Works for items queued by older app versions too.
    formData.append('uploadId', entry.opId);
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/tally/documents/upload/${masterId}`, formData));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] UPLOAD_DOCUMENT synced clientUuid=${entry.clientUuid} masterId=${masterId}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  // No idMap resolution needed here (unlike replayUploadDocument's "doc-" branch, which still
  // has to handle a pre-existing-master-but-offline-device case the same way): every
  // UPLOAD_PRODUCT_PHOTO entry is only ever enqueued against an already-real encodedId —
  // ProductComponent blocks queuing a photo against a still-provisional (offline-created,
  // not yet synced) product client-side instead. See queueProductPhotoUpload's comment.
  private async replayUploadProductPhoto(entry: OutboxEntry): Promise<ReplayResult> {
    const payload = entry.payload as { encodedId: string; file: File };
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('uploadId', entry.opId); // no duplicate photo on retry (Phase 2)
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/products/${payload.encodedId}/photos`, formData));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] UPLOAD_PRODUCT_PHOTO synced clientUuid=${entry.clientUuid}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  // Custom-Made / re-saw request queued offline (CustomMadeRequestDialogComponent.submitOffline).
  // clientUuid prefix tells us which case this is, same convention as replayUploadDocument/
  // replaySaveTallySettings: a synthetic `cmr-<uuid>` id means the row's own TU was already real
  // when this was queued (no dependency, sourceTallyMasterId is already a real id); anything else
  // is the owning TU's own clientUuid, needing idMap resolution first — the whole reason this
  // engine now runs multi-pass (see runSync) is so this 3-level dependency (CREATE_TU ->
  // SAVE_TALLY_DETAILS -> CREATE_PROCESSING_RUN) gets a fair re-attempt once its TU has synced,
  // even if this entry happened to come before that TU's own CREATE_TU in outbox order.
  // The payload's OWN clientUuid field (distinct from entry.clientUuid) is the backend
  // idempotency key from Phase 4a (ProcessingRun.clientUuid / ProcessingRunServiceImpl.createRun)
  // — sent as-is, untouched by either branch below.
  private async replayCreateProcessingRun(entry: OutboxEntry): Promise<ReplayResult> {
    const payload = entry.payload as any;
    let requestBody = payload;

    if (!entry.clientUuid.startsWith('cmr-')) {
      const idMapEntry = await offlineDb.idMap.get(entry.clientUuid);
      if (!idMapEntry) {
        return 'pending'; // owning CREATE_TU hasn't synced yet — try again next pass/run
      }
      const realSourceTallyMasterId = idMapEntry.tallyMasterIdMap[payload.sourceTallyMasterId]
        ?? idMapEntry.defaultTallyMasterId
        ?? payload.sourceTallyMasterId;
      requestBody = { ...payload, sourceTallyMasterId: realSourceTallyMasterId };
    }

    try {
      const response: any = await firstValueFrom(this.http.post(`${this.apiUrl}/processing-runs`, requestBody));
      await this.outboxService.markSynced(entry.seq!);
      this.logger.info(`[SyncEngine] CREATE_PROCESSING_RUN synced clientUuid=${payload.clientUuid} runId=${response.id}`);
      return 'synced';
    } catch (error: any) {
      return this.handleReplayError(entry, error);
    }
  }

  private async handleReplayError(entry: OutboxEntry, error: any): Promise<ReplayResult> {
    const attempts = (entry.attempts || 0) + 1;
    const message = error?.error?.message || error?.message || 'Unknown error';
    // Login expired (the interceptor's own refresh already failed): not the item's fault — keep it waiting
    // and stop the run; it continues after the user logs in again (OFFLINE_PLAN.md 1.2).
    // Three shapes reach here when the login is over: a plain 401; the failed /auth/refresh call's own error
    // (the interceptor passes that on, whatever its status); and an EmptyError, when the interceptor ends
    // the request quietly because this session was taken over by a login elsewhere.
    const loginOver = error?.status === 401
      || String(error?.url ?? '').includes('/auth/refresh')
      || error?.name === 'EmptyError';
    if (loginOver) {
      this.logger.info(`[SyncEngine] ${entry.opType} paused — login expired, will continue after the next login`);
      return 'auth';
    }
    // Never reached the server (connection gone, Wi-Fi without internet): not a failed try — the run
    // stops and retries shortly, without counting towards the retry limit (Phase 2).
    if (error?.status === 0) {
      this.logger.debug(`[SyncEngine] ${entry.opType} not sent — server unreachable, will retry shortly`);
      return 'unreachable';
    }
    // 0 = network-level failure (offline/timeout), 5xx = server error, 408/429 = also worth
    // retrying — everything else (validation, plan-limit, etc.) is terminal.
    const isRetryable = error?.status === 0 || error?.status >= 500 || error?.status === 408 || error?.status === 429;

    if (!isRetryable) {
      const body = error?.error;
      const errorCode: string | undefined = body?.code;
      const errorMeta = errorCode === 'NAME_ALREADY_EXISTS'
        ? { existingId: body?.existingId, existingEncodedId: body?.existingEncodedId }
        : undefined;
      await this.outboxService.markRejected(entry.seq!, message, errorCode, errorMeta);
      this.logger.warn(`[SyncEngine] ${entry.opType} rejected clientUuid=${entry.clientUuid}: ${message}`);
      return 'rejected';
    }
    await this.outboxService.markFailedAttempt(entry.seq!, attempts, message);
    this.logger.debug(`[SyncEngine] ${entry.opType} failed, will retry, clientUuid=${entry.clientUuid}: ${message}`);
    return 'pending';
  }
}
